import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotifcationService } from '../../graphql/services/notification.service';
import { NotificationStatus } from '../../graphql/generated/schema';

export interface SevereWeatherAlert {
  notificationId: string;
  message: string;
  isVisible: boolean;
}

const FIRE_TITLE = 'severe_weather_warning';
const RESOLVED_TITLE = 'severe_weather_resolved';

@Injectable({ providedIn: 'root' })
export class SevereWeatherNotificationService {
  private currentAlert = new BehaviorSubject<SevereWeatherAlert | null>(null);
  private destroy$ = new Subject<void>();

  /**
   * Rows this service has finished with. Marking them read is what normally takes
   * them out of criticalNotifications, but that is a server round-trip that can
   * fail - and this stream re-emits on any unrelated message being read. Without
   * a local record, a warning whose mark-read did not land would be re-found on
   * the next emission and pop a banner for weather that has already passed.
   */
  private retiredIds = new Set<string>();

  currentAlert$: Observable<SevereWeatherAlert | null> = this.currentAlert.asObservable();

  constructor(
    private ngZone: NgZone,
    private notificationService: NotifcationService,
  ) {}

  init(): void {
    // Mirrors WaterLeakNotificationService's stream, but no query state machine here.
    this.notificationService.criticalNotifications
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        if (!notifications?.length) return;

        const weatherNotification = notifications
          .filter(n => !this.retiredIds.has(n.id))
          .find(n => n.title === FIRE_TITLE || n.title === RESOLVED_TITLE);
        if (!weatherNotification) return;

        if (weatherNotification.title === FIRE_TITLE && !this.currentAlert.value) {
          this.showAlert({
            notificationId: weatherNotification.id,
            message: weatherNotification.text || 'A severe weather warning has been issued for your building.',
            isVisible: true,
          });
        } else if (weatherNotification.title === RESOLVED_TITLE) {
          // No guard on currentAlert here: the resolved row usually arrives
          // AFTER the resident dismissed the banner, so requiring a visible
          // banner left it unread and Critical forever, pinning the Messages
          // widget open. A resolved row the resident deliberately un-read is
          // filtered out of criticalNotifications upstream instead, so it never
          // reaches this branch to be undone.
          const shownId = this.currentAlert.value?.notificationId;
          this.dismiss();

          // dismiss() can only retire the row behind a banner that was actually
          // shown. When a warning and its resolution both land while the app is
          // closed there was never a banner, so the warning would stay unread and
          // Critical - and the moment the resolved row leaves this stream (it is
          // marked read just below) the warning becomes the newest match and pops
          // a banner for weather that has already passed. find() returned the
          // resolved row as the newest of the two, so every warning still in this
          // list is older than it and is retired here.
          //
          // Rows the resident deliberately un-read are already filtered out of
          // criticalNotifications, so this cannot undo that choice.
          const retire = new Set(notifications.filter(n => n.title === FIRE_TITLE).map(n => n.id));
          retire.add(weatherNotification.id);
          if (shownId) this.retiredIds.add(shownId);
          retire.delete(shownId!);
          retire.forEach(id => {
            this.retiredIds.add(id);
            this.notificationService.updateNotfifcation(id, NotificationStatus.Read).subscribe();
          });
        }
      });

    // Trigger notification load in case alerts-widget hasn't mounted yet.
    // Safe to call multiple times - guards against double-subscription internally.
    this.notificationService.initNotifications();
  }

  showAlert(alert: SevereWeatherAlert): void {
    this.ngZone.run(() => {
      this.currentAlert.next(alert);
    });
  }

  dismiss(): void {
    const notificationId = this.currentAlert.value?.notificationId;
    this.ngZone.run(() => {
      this.currentAlert.next(null);
    });
    if (notificationId) {
      this.notificationService.updateNotfifcation(notificationId, NotificationStatus.Read).subscribe();
    }
  }

  cleanup(): void {
    this.retiredIds.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
